import { Fragment, useMemo, useState } from "react";
import { EmptyState } from "@/components/feedback/empty/EmptyState";
import { ErrorBlock } from "@/components/feedback/error/AlertError";
import { AdvancedSkeleton } from "@/components/feedback/loading/Skeletons";
import { Badge } from "@/components/ui/others/Badges";
import { Separator } from "@/components/ui/separator";
import useRolesPipeline from "@/features/users/api/useRolesPipeline";
import { isBlockedUser } from "@/features/users/utils/blockedUsers";
import { ChevronRight } from "lucide-react";
import {
  effectiveAssignKind,
  holderLevelBadge,
  roleAcceptsAssignment,
} from "@/features/leads/hooks/useAssignationPermissions";
import {
  buildRolePipelineParams,
  type AssignFilters,
} from "./FilterSection";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  selectAccessConfig,
  selectCanBypassRbacChecks,
} from "@/rbac/rbacSlice";
import {
  clearSelectedLeadIds,
  setIsAssignModalOpen,
} from "@/features/leads/stores/leadFunctionalSlice";
import useTracksPipeline from "@/features/stage/api/useTracksPipeline";
import { assignableTrackIds } from "../trackAssign";
import TrackAssignDialog from "./TrackAssignDialog";

export default function RolesList({
  setSelectedRole,
  kind = "lead",
  filters,
  searchTerm = "",
  trackOnly = false,
  onTrackAssigned,
  peerRow,
}: {
  setSelectedRole: (role: string) => void;
  /** What's being handed off — drives the inbound filter on candidates. */
  kind?: "lead" | "child";
  filters?: AssignFilters;
  searchTerm?: string;
  /** The viewer may ONLY hand out tracks (Manage on a track, Outbound off):
   *  every role that is not one of their track-member roles is left out. */
  trackOnly?: boolean;
  /** Host override for what happens after a track hand-over. Default (the
   *  Assign drawer): un-tick the leads and close the drawer. Bulk Update
   *  passes its own so the selection survives for the rest of the bulk. */
  onTrackAssigned?: () => void;
  /** "Transfer to peers": the viewer's OWN role, offered on top when their
   *  role grants `outbound_peer` and they hold this slot. The role pipeline
   *  never lists the viewer's own role (only roles below theirs), so without
   *  this row a role at the bottom of the ladder — a counsellor with both
   *  toggles on — opened an empty picker and could not hand over at all.
   *  `count` is the server's eligible-peer count (null while loading). */
  peerRow?: { roleName: string; count: number | null; onPick: () => void };
}) {
  const serverParams = useMemo(
    // forFilters drops roles the viewer's role EXCLUDES in the hierarchy
    // ladder — an excluded role must not be assignable from here either.
    () =>
      filters
        ? { ...buildRolePipelineParams(filters), forFilters: 1 }
        : { withLeadCount: "true", forFilters: 1 },
    [filters],
  );
  const { rolesPipeline, isLoading, error, refetch } = useRolesPipeline(
    undefined,
    serverParams,
  );

  // Compute every role with the reason it would be selectable, dropped, or
  // shown as disabled. We deliberately keep roles in the list even when they
  // can't be picked, so the admin can see WHY a role isn't selectable — the
  // old "silently disappear" behaviour caused real support tickets where a
  // role's `inbound_lead: deny` toggle was the actual cause but the popup
  // gave no clue.
  // TRACK-MEMBER roles (Own on a track): pickable as a TRACK assignment by
  // admins and by roles with Manage on that track — independent of the
  // Outbound/Inbound lead-assignation switches.
  const dispatch = useAppDispatch();
  const viewerAccessConfig = useAppSelector(selectAccessConfig);
  const viewerBypass = useAppSelector(selectCanBypassRbacChecks);
  const selectedLeadIds = useAppSelector(
    (state) => state.leadFunctional.selectedLeadIds
  );
  const { tracks } = useTracksPipeline({ viewable: true });
  const [trackAssign, setTrackAssign] = useState<{
    roleId: number | string;
    roleName: string;
    trackIds: number[];
  } | null>(null);

  const visibleRoles = useMemo(() => {
    if (!Array.isArray(rolesPipeline)) return rolesPipeline;
    const status = filters?.status ?? "active";
    const term = searchTerm.trim().toLowerCase();
    return rolesPipeline
      .map((role: any) => {
        // Blocked users are not offered anywhere in assignment (user
        // directive 2026-09-20), so they must not be COUNTED here either —
        // otherwise a role of one blocked person reads "1 user" and opens an
        // empty list.
        const assignable = (role?.users ?? []).filter(
          (u: any) => !isBlockedUser(u)
        );
        const totalUsers = assignable.length;
        const filteredUsers = assignable.filter((u: any) => {
          if (status === "active" && u.isActive === false) return false;
          if (status === "inactive" && u.isActive !== false) return false;
          if (term && !String(u.name ?? "").toLowerCase().includes(term))
            return false;
          return true;
        });
        // "Lead user / Deal user": a deal-user role picked on a lead lands on
        // its deals (and a lead-user role picked on a deal on the lead), so
        // its inbound toggle is judged at THAT level.
        const accepts = roleAcceptsAssignment(role, effectiveAssignKind(role, kind));
        // A role that cannot RECEIVE leads but holds a track at Own is
        // offered as a track assignment instead of being greyed out. Roles
        // that do accept leads keep the normal flow untouched.
        const trackIds =
          (!accepts || trackOnly) && kind === "lead"
            ? assignableTrackIds(role, {
                bypass: viewerBypass,
                accessConfig: viewerAccessConfig,
              })
            : [];
        let disabledReason: string | null = null;
        if (trackIds.length > 0 && totalUsers > 0) {
          disabledReason = null;
        } else if (!accepts) {
          disabledReason = "Receiving disabled in role settings";
        } else if (totalUsers === 0) {
          disabledReason = "No users in this role";
        } else if (filteredUsers.length === 0) {
          disabledReason =
            status === "active"
              ? "All users in this role are inactive"
              : "No users match the current filter";
        }
        return {
          ...role,
          users: filteredUsers,
          totalUsers,
          disabledReason,
          trackIds,
          holderBadge: holderLevelBadge(role),
        };
      })
      // Hide roles that have no users at all when search term is present —
      // search is an explicit narrow, so empty hits would be noise. Otherwise
      // keep everything: visible or disabled-with-reason.
      .filter((role: any) => {
        if (trackOnly && !role.trackIds?.length) return false;
        if (term) return role.users.length > 0;
        return true;
      });
  }, [
    rolesPipeline,
    kind,
    filters?.status,
    searchTerm,
    viewerBypass,
    viewerAccessConfig,
    trackOnly,
  ]);

  if (isLoading) {
    return (
      <div className="pt-2 pb-4">
        <AdvancedSkeleton variant="table" rows={15} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <ErrorBlock message={error.message} retry={refetch} />
      </div>
    );
  }

  const noRoles = !visibleRoles || visibleRoles.length === 0;
  if (noRoles && !peerRow) {
    // Nothing narrowed the list → the server itself offered no role: a
    // non-admin only ever sees roles BELOW their own, so a role at the bottom
    // of the ladder has nobody to assign to. Say that, instead of blaming
    // filters the viewer never touched.
    const narrowed =
      Boolean(searchTerm.trim()) ||
      Boolean(filters?.branchId) ||
      (filters?.status ?? "active") !== "active" ||
      Object.values(filters?.customFields ?? {}).some(Boolean);
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <EmptyState
          title="No Roles Found"
          description={
            trackOnly
              ? "No track-member roles to hand this track to."
              : narrowed
                ? "No roles match the current filters. Adjust filters or check the Inbound toggles in role permissions."
                : 'There are no roles below yours to assign to. To hand a lead you hold to someone on your own role, turn on "Transfer to peers" for your role.'
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {peerRow && (
        <Fragment key="__peers__">
          <div
            className="flex items-center gap-2 justify-between px-3 cursor-pointer"
            onClick={peerRow.onPick}
            title="Hand over what you hold on this lead to someone on your own role"
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="flex items-center gap-2">
                <span>{peerRow.roleName}</span>
                <Badge variant="green">{peerRow.count ?? "…"}</Badge>
                <Badge
                  variant="info"
                  title="Transfer to peers — same-role hand-over; you lose access once transferred"
                >
                  Your peers
                </Badge>
              </div>
              <span className="text-[11px] text-[#156548]">
                Hand over to a user on your role · you lose access once transferred
              </span>
            </div>
            <ChevronRight className="size-5" />
          </div>
          <Separator variant="dotted" />
        </Fragment>
      )}
      {visibleRoles?.map((role: any) => {
        const isDisabled = Boolean(role.disabledReason);
        return (
          <Fragment key={role.id}>
            <div
              className={`flex items-center gap-2 justify-between px-3 ${
                isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
              onClick={
                isDisabled
                  ? undefined
                  : role.trackIds?.length
                    ? () =>
                        setTrackAssign({
                          roleId: role.id,
                          roleName: role.name,
                          trackIds: role.trackIds,
                        })
                    : () => setSelectedRole(role.id)
              }
              title={role.disabledReason ?? undefined}
            >
              <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={isDisabled ? "text-(--color-black-7,#505e59)" : undefined}>
                    {role.name}
                  </span>
                  <Badge variant={isDisabled ? "gray" : "green"}>
                    {role?.users?.length ?? 0}
                  </Badge>
                  {role.holderBadge && (
                    <Badge
                      variant={isDisabled ? "gray" : "info"}
                      title={
                        role.holderBadge === "Deal user"
                          ? "Assigned on the lead's deals — no lead-or-deal question"
                          : "Always assigned at lead level"
                      }
                    >
                      {role.holderBadge}
                    </Badge>
                  )}
                </div>
                {!isDisabled && role.trackIds?.length > 0 && (
                  <span className="text-[11px] text-[#156548]">
                    Track member ·{" "}
                    {role.trackIds
                      .map(
                        (id: number) =>
                          (tracks ?? []).find(
                            (t: { id: number }) => t.id === id
                          )?.name ?? `Track #${id}`
                      )
                      .join(", ")}
                  </span>
                )}
                {role.disabledReason && (
                  <span className="text-[11px] text-[#e8a24f]">
                    {role.disabledReason}
                  </span>
                )}
              </div>
              {!isDisabled && <ChevronRight className="size-5" />}
            </div>
            <Separator variant="dotted" />
          </Fragment>
        );
      })}

      <TrackAssignDialog
        open={trackAssign !== null}
        onOpenChange={(open) => !open && setTrackAssign(null)}
        roleId={trackAssign?.roleId ?? null}
        roleName={trackAssign?.roleName}
        trackIds={trackAssign?.trackIds ?? []}
        leadIds={selectedLeadIds}
        onDone={() => {
          setTrackAssign(null);
          if (onTrackAssigned) return onTrackAssigned();
          dispatch(clearSelectedLeadIds());
          dispatch(setIsAssignModalOpen(false));
        }}
      />
    </div>
  );
}
